import { ExperienceType } from '@ninetailed/experience.js-shared';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import { getVariantDisplayName } from './getVariantDisplayName';

interface NinetailedInlineTrackerProps {
  experienceName: string;
  experienceSnowflakeName: string | null;
  experienceId: string;
  variantName: string;
  variantId: string;
  variantIndex: number;
  experienceType?: ExperienceType | undefined;
  nonce?: string;
}

/**
 * Server component that renders an inline <script> to fire the ninetailed_experience_viewed
 * analytics event. This bypasses React entirely, so it survives hydration failures caused by
 * Meta in-app browsers (Facebook/Instagram) injecting DOM elements before React hydrates.
 */
export function NinetailedInlineTracker({
  experienceName,
  experienceSnowflakeName,
  experienceId,
  variantName,
  variantId,
  variantIndex,
  experienceType,
  nonce,
}: NinetailedInlineTrackerProps) {
  // Only track when user is bucketed into an experience
  if (experienceType === undefined) {
    return null;
  }
  const displayName = getVariantDisplayName(variantIndex, variantName, experienceType);
  const experienceDisplayName = experienceSnowflakeName || experienceName;
  const cookieName = NINETAILED_ANONYMOUS_ID_COOKIE;

  // Serialize tracking data for the inline script.
  // Replace `<` with `\u003c` to prevent `</script>` in CMS values from breaking out of the script tag.
  const trackingData = JSON.stringify({
    experience_name: experienceDisplayName,
    experience_id: experienceId,
    variant_name: displayName,
    variant_id: variantId,
  }).replace(/</g, '\\u003c');

  return (
    <script
      nonce={nonce}
      suppressHydrationWarning
      data-nt-experience-tracker={experienceId}
      dangerouslySetInnerHTML={{
        __html: `
(function() {
  var data = ${trackingData};
  var cookieName = ${JSON.stringify(cookieName).replace(/</g, '\\u003c')};

  function getCookieValue(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  }

  data.ninetailed_id = getCookieValue(cookieName);

  var attempts = 0;
  function fire() {
    attempts++;
    if (window.Track && window.analyticsHandler) {
      window.analyticsHandler.trackEvent(window.Track.WebActionTriggered, {
        unique_id: 'ninetailed_experience_viewed',
        item_details: {
          ninetailed_experience_viewed: data
        }
      });
    } else if (attempts < 3000) {
      setTimeout(fire, 1);
    } else {
      console.warn('[NinetailedInlineTracker] Analytics SDK not available after 3000 attempts, ninetailed_experience_viewed not sent for experience: ' + data.experience_id);
    }
  }
  fire();
})();
`,
      }}
    />
  );
}
